import { useMemo, useState } from "react";
import { ExternalLink, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/library/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import useLeadSource from "@/features/sourceAndCampaign/api/useLeadSource";
import { formatLeadPhone } from "@/features/leads/utilities/formatLeadPhone";
import {
  fillChatLink,
  isSafeChatLink,
  openChatWindow,
  pickChatLinkSource,
} from "@/features/sourceAndCampaign/utils/chatLink";

interface ChatLinkLead {
  id?: number | string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | number | null;
  phoneCountryCode?: string | null;
  leadSource?: { id?: number | string | null } | null;
}

interface SourceRow {
  id: number;
  name?: string | null;
  isActive?: boolean | null;
  config?: unknown;
}

/**
 * Header chat button for a lead whose source has a Chat link (source editor →
 * "Chat link"), or for ANY lead when a source's link is marked "every lead":
 * opens the lead's conversation in that chat tool, as a popup window or — when
 * the source says so — a popup inside Xale.
 */
export default function LeadChatLinkButton({ lead }: { lead: ChatLinkLead | null | undefined }) {
  const { leadSources } = useLeadSource();
  const [insideOpen, setInsideOpen] = useState(false);

  const sourceId = lead?.leadSource?.id;
  const picked = useMemo(
    () => pickChatLinkSource(leadSources as SourceRow[] | undefined, sourceId),
    [leadSources, sourceId],
  );
  const source = picked?.source ?? null;
  const chatLink = picked?.chatLink ?? null;

  const url = useMemo(() => {
    if (!chatLink || !lead?.phone) return null;
    const filled = fillChatLink(chatLink.url, lead);
    return isSafeChatLink(filled) ? filled : null;
  }, [chatLink, lead]);

  if (!source || !chatLink || !url) return null;

  const sourceName = String(source?.name ?? "chat");

  const openWindow = () => {
    if (!openChatWindow(url, source.id)) {
      toast.error("Your browser blocked the chat window — allow pop-ups for Xale and try again");
    }
  };

  const handleClick = () => {
    if (chatLink.openIn === "inside") setInsideOpen(true);
    else openWindow();
  };

  return (
    <>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 rounded-lg px-2.5 hover:bg-accent/50"
            style={{
              backgroundImage:
                "linear-gradient(160.304deg, rgba(38, 115, 85, 0.01) 110.38%, rgba(38, 115, 85, 0.12) 110.92%)",
            }}
            onClick={handleClick}
            aria-label={`Open chat in ${sourceName}`}
          >
            <MessageSquareText className="size-4 text-[var(--color-black-8)]" strokeWidth={1.5} />
            <span className="text-xs font-medium text-[var(--color-black-8)]">Chat</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="max-w-[240px]">
          <div className="text-[12px] leading-snug">
            <div className="font-semibold mb-0.5">Open chat · {sourceName}</div>
            <div className="text-[var(--color-black-7)]">
              Opens this lead's conversation in {sourceName}. You need to be logged in to it in
              this browser.
            </div>
          </div>
        </TooltipContent>
      </Tooltip>

      {chatLink.openIn === "inside" && (
        <Dialog open={insideOpen} onOpenChange={setInsideOpen}>
          <DialogContent className="!max-w-[min(1100px,95vw)] w-[95vw] h-[85vh] p-0 gap-0 overflow-hidden flex flex-col">
            <div className="flex items-center gap-3 px-4 py-3 pr-12 border-b border-[var(--color-black-2)]">
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-[15px] font-medium truncate">
                  {sourceName} · {lead?.name}
                </DialogTitle>
                <p className="text-xs text-[var(--color-black-6)] truncate">
                  {formatLeadPhone(lead?.phone, lead?.phoneCountryCode)} — blank or refused? {sourceName}{" "}
                  doesn't allow being shown inside Xale; open it in a window instead.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => {
                  setInsideOpen(false);
                  openWindow();
                }}
              >
                <ExternalLink className="size-3.5 mr-1.5" strokeWidth={1.75} />
                Open in window
              </Button>
            </div>
            {/* No allow-top-navigation: the chat page can never take over Xale's tab. */}
            <iframe
              key={url}
              src={url}
              title={`${sourceName} chat`}
              className="flex-1 w-full border-0 bg-white"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals"
              allow="clipboard-write"
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
